import { useState, useEffect, useCallback, useRef } from 'react';
import type { RateCardSummary, RoleRate, AssignedPerson, UpdateRoleRateRequest, UpdatePersonRateRequest, RateChangeHistoryEntry } from '@/types/rateCard.types';
import { calculateMargin } from '@/types/rateCard.types';
import type { PersonBudget, RoleBudget } from '@/types/budget.types';
import { RateCardStorageService } from '@/services/rateCardStorage.service';
import { getDataMode, getApiBaseUrl, fetchBudgetData } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

interface BackendPersonRate {
  employeeId: number;
  employeeName: string;
  roleId: number;
  roleName: string;
  costRateOverride: number | null;
  billRateOverride: number | null;
  dailyCostRateOverride: number | null;
  dailyBillRateOverride: number | null;
  defaultCostRate: number | null;
  defaultBillRate: number | null;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
}

interface BackendRateCardEntry {
  roleId: number;
  roleName: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
  projectRoleRateId: number | null;
  projectCostOverride: number | null;
  projectBillOverride: number | null;
  projectDailyCostOverride: number | null;
  projectDailyBillOverride: number | null;
  effectiveCostRate: number;
  effectiveBillRate: number;
  effectiveDailyCostRate: number | null;
  effectiveDailyBillRate: number | null;
  source: string;
}

interface ResourceBudgetRoleData {
  byRole: RoleBudget[];
  byPerson: PersonBudget[];
}

async function fetchResourceBudgetOnly(projectId: number): Promise<ResourceBudgetRoleData | null> {
  const mode = getDataMode();

  if (mode === 'demo') {
    const budgetData = await fetchBudgetData(projectId);
    if (!budgetData?.resourceBudget) return null;
    return {
      byRole: budgetData.resourceBudget.byRole,
      byPerson: budgetData.resourceBudget.byPerson,
    };
  }

  try {
    const baseUrl = getApiBaseUrl();
    const rb = await apiFetch<{ id: string | number }>(`${baseUrl}/ResourceBudgets/by-project/${projectId}`);

    if (!rb?.id) return null;

    const resourceBudgetId = rb.id;
    const [roleBudgetsResult, personBudgetsResult] = await Promise.allSettled([
      apiFetch<{
        id: string; resourceBudgetId: string; roleId: number | null; roleName: string;
        budgetHours: number; actualHours: number; remainingHours: number; percentUsed: number;
        billRate: number; budgetCost: number; actualCost: number; monthlyHours?: string;
      }[]>(`${baseUrl}/RoleBudgets/by-resource-budget/${resourceBudgetId}`),
      apiFetch<{
        id: string; resourceBudgetId: string; personId: string; personName: string;
        roleId?: number; roleName?: string; budgetHours: number; actualHours: number;
        utilisation: number; costRate: number; budgetCost: number; actualCost: number;
      }[]>(`${baseUrl}/PersonBudgets/by-resource-budget/${resourceBudgetId}`),
    ]);

    const roleBudgets = roleBudgetsResult.status === 'fulfilled' ? roleBudgetsResult.value : [];
    const personBudgets = personBudgetsResult.status === 'fulfilled' ? personBudgetsResult.value : [];

    return {
      byRole: roleBudgets.map(r => ({
        id: typeof r.id === 'number' ? r.id : parseInt(String(r.id), 10),
        roleId: r.roleId ?? undefined,
        role: r.roleName || '',
        budgetHours: r.budgetHours,
        allocatedHours: r.budgetHours,
        actualHours: r.actualHours,
        remainingHours: r.remainingHours,
        percentUsed: r.percentUsed,
        billRate: r.billRate,
        budgetCost: r.budgetCost,
        actualCost: r.actualCost,
      })),
      byPerson: personBudgets.map(pb => ({
        id: typeof pb.id === 'number' ? pb.id : parseInt(String(pb.id), 10),
        personId: typeof pb.personId === 'number' ? pb.personId : parseInt(String(pb.personId), 10),
        personName: pb.personName,
        role: pb.roleName || '',
        budgetHours: pb.budgetHours,
        actualHours: pb.actualHours,
        utilisation: pb.utilisation,
        costRate: pb.costRate,
        budgetCost: pb.budgetCost,
        actualCost: pb.actualCost,
      })),
    };
  } catch (error) {
    console.warn('[useRateCard] Failed to fetch resource budget:', error);
    return null;
  }
}

function transformBackendToRoleRates(entries: BackendRateCardEntry[]): RoleRate[] {
  return entries.map((entry) => ({
    roleId: `role-${entry.roleId}`,
    roleName: entry.roleName,
    hourlyCostRate: entry.effectiveCostRate,
    hourlyBillRate: entry.effectiveBillRate,
    dailyCostRate: entry.effectiveDailyCostRate ?? entry.defaultDailyCostRate ?? 0,
    dailyBillRate: entry.effectiveDailyBillRate ?? entry.defaultDailyBillRate ?? 0,
    margin: calculateMargin(entry.effectiveBillRate, entry.effectiveCostRate),
    effectiveFrom: new Date().toISOString(),
    source: (entry.source || 'Org Default') as 'Org Default' | 'Project Override' | 'Person Override',
    assignedPeople: [],
  }));
}

function computeSummary(roleRates: RoleRate[]): RateCardSummary {
  if (roleRates.length === 0) {
    return {
      averageHourlyBillRate: 0,
      averageHourlyCostRate: 0,
      averageDailyBillRate: 0,
      averageDailyCostRate: 0,
      averageMargin: 0,
      roleCount: 0,
      lastUpdated: new Date().toISOString(),
      lastUpdatedBy: 'System',
    };
  }

  const totalHourlyBill = roleRates.reduce((sum, r) => sum + r.hourlyBillRate, 0);
  const totalHourlyCost = roleRates.reduce((sum, r) => sum + r.hourlyCostRate, 0);
  const totalDailyBill = roleRates.reduce((sum, r) => sum + r.dailyBillRate, 0);
  const totalDailyCost = roleRates.reduce((sum, r) => sum + r.dailyCostRate, 0);

  const avgHourlyBill = totalHourlyBill / roleRates.length;
  const avgHourlyCost = totalHourlyCost / roleRates.length;

  return {
    averageHourlyBillRate: Math.round(avgHourlyBill * 100) / 100,
    averageHourlyCostRate: Math.round(avgHourlyCost * 100) / 100,
    averageDailyBillRate: Math.round((totalDailyBill / roleRates.length) * 100) / 100,
    averageDailyCostRate: Math.round((totalDailyCost / roleRates.length) * 100) / 100,
    averageMargin: calculateMargin(avgHourlyBill, avgHourlyCost),
    roleCount: roleRates.length,
    lastUpdated: new Date().toISOString(),
    lastUpdatedBy: 'Current User',
  };
}

export const useRateCard = (projectId: string) => {
  const [summary, setSummary] = useState<RateCardSummary>({
    averageHourlyBillRate: 0,
    averageHourlyCostRate: 0,
    averageDailyBillRate: 0,
    averageDailyCostRate: 0,
    averageMargin: 0,
    roleCount: 0,
    lastUpdated: new Date().toISOString(),
    lastUpdatedBy: 'System',
  });
  const [roleRates, setRoleRates] = useState<RoleRate[]>([]);
  const [rateChangeHistory, setRateChangeHistory] = useState<RateChangeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const backendEntriesRef = useRef<BackendRateCardEntry[]>([]);
  const loadInProgressRef = useRef(false);

  const mode = getDataMode();

  const loadDemoData = useCallback(async () => {
    const resBudget = await fetchResourceBudgetOnly(parseInt(projectId));
    const allocatedRoles = resBudget?.byRole?.map(role => role.role) || [];
    const allocatedPeople = resBudget?.byPerson?.map(person => ({
      personId: person.personId.toString(),
      personName: person.personName,
      role: person.role,
    })) || [];

    const filter = allocatedRoles.length > 0 ? { allocatedRoles, allocatedPeople } : undefined;

    const summaryData = RateCardStorageService.getRateCardSummary(projectId, filter);
    const roleRatesData = RateCardStorageService.getAllRoleRates(projectId, filter);

    setSummary(summaryData);
    setRoleRates(roleRatesData);
  }, [projectId]);

  const loadLiveData = useCallback(async () => {
    const [rateCardResponse, resBudget] = await Promise.all([
      apiFetch<BackendRateCardEntry[]>(
        `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}`
      ),
      fetchResourceBudgetOnly(parseInt(projectId)),
    ]);

    let entries = Array.isArray(rateCardResponse) ? rateCardResponse : [];

    const resourceBudgetRoles = resBudget?.byRole;
    if (resourceBudgetRoles && resourceBudgetRoles.length > 0) {
      const budgetRoleIds = new Set(
        resourceBudgetRoles
          .filter(r => r.roleId != null)
          .map(r => r.roleId!)
      );
      const budgetRoleNames = new Set(
        resourceBudgetRoles.map(r => r.role.toLowerCase())
      );

      entries = entries.filter(entry =>
        budgetRoleIds.has(entry.roleId) ||
        budgetRoleNames.has(entry.roleName.toLowerCase())
      );
    }

    backendEntriesRef.current = entries;
    const rates = transformBackendToRoleRates(entries);

    let personRateOverrides: BackendPersonRate[] = [];
    try {
      const prResponse = await apiFetch<BackendPersonRate[]>(
        `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/person-rates`
      );
      personRateOverrides = Array.isArray(prResponse) ? prResponse : [];
    } catch {
      personRateOverrides = [];
    }

    const allocatedPeople = resBudget?.byPerson || [];
    if (allocatedPeople.length > 0) {
      for (const rate of rates) {
        const matchingPeople = allocatedPeople.filter(
          p => p.role.toLowerCase() === rate.roleName.toLowerCase()
        );
        rate.assignedPeople = matchingPeople.map((person: PersonBudget): AssignedPerson => {
          const override = personRateOverrides.find(
            pr => pr.employeeId === person.personId && pr.roleName.toLowerCase() === rate.roleName.toLowerCase()
          );

          const baseCost = person.costRate || rate.hourlyCostRate;
          const baseBill = rate.hourlyBillRate;
          const baseDailyCost = rate.dailyCostRate;
          const baseDailyBill = rate.dailyBillRate;

          const effectiveCost = override?.costRateOverride ?? baseCost;
          const effectiveBill = override?.billRateOverride ?? baseBill;
          const effectiveDailyCost = override?.dailyCostRateOverride ?? baseDailyCost;
          const effectiveDailyBill = override?.dailyBillRateOverride ?? baseDailyBill;

          return {
            personId: person.personId.toString(),
            personName: person.personName,
            hourlyCostRate: effectiveCost,
            hourlyBillRate: effectiveBill,
            dailyCostRate: effectiveDailyCost,
            dailyBillRate: effectiveDailyBill,
            margin: calculateMargin(effectiveBill, effectiveCost),
            effectiveFrom: new Date().toISOString(),
            source: override ? 'Person Override' : 'Org Default',
          };
        });
      }
    }

    const summaryData = computeSummary(rates);

    setRoleRates(rates);
    setSummary(summaryData);
  }, [projectId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'live') {
        await loadLiveData();
      } else {
        await loadDemoData();
      }
    } catch (error) {
      console.error('Error loading rate card data:', error);
    } finally {
      setLoading(false);
    }
  }, [mode, loadLiveData, loadDemoData]);

  useEffect(() => {
    if (loadInProgressRef.current) return;
    loadInProgressRef.current = true;
    loadData().finally(() => {
      loadInProgressRef.current = false;
    });
  }, [loadData]);

  const updateRoleRate = (request: UpdateRoleRateRequest) => {
    if (mode === 'live') {
      const dbRoleId = parseInt(request.roleId.replace('role-', ''), 10);
      const entry = backendEntriesRef.current.find(e => e.roleId === dbRoleId);
      if (!entry) return false;

      const payload = {
        costRateOverride: request.hourlyCostRate,
        billRateOverride: request.hourlyBillRate,
        dailyCostRateOverride: request.dailyCostRate,
        dailyBillRateOverride: request.dailyBillRate,
        notes: request.notes,
      };

      apiFetch(`${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/role/${dbRoleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(() => loadData()).catch(err => console.error('Error updating role rate:', err));

      return true;
    }
    try {
      RateCardStorageService.updateRoleRate(projectId, request);
      loadData();
      return true;
    } catch (error) {
      console.error('Error updating role rate:', error);
      return false;
    }
  };

  const updatePersonRate = (request: UpdatePersonRateRequest) => {
    if (mode === 'live') {
      const dbRoleId = parseInt(request.roleId.replace('role-', ''), 10);
      const employeeId = parseInt(request.personId, 10);

      const payload = {
        costRateOverride: request.hourlyCostRate,
        billRateOverride: request.hourlyBillRate,
        dailyCostRateOverride: request.dailyCostRate,
        dailyBillRateOverride: request.dailyBillRate,
        notes: request.notes,
        personName: request.personName,
      };

      apiFetch(`${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/person/${employeeId}/role/${dbRoleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(() => loadData()).catch(err => console.error('Error updating person rate:', err));

      return true;
    }
    try {
      RateCardStorageService.updatePersonRate(projectId, request);
      loadData();
      return true;
    } catch (error) {
      console.error('Error updating person rate:', error);
      return false;
    }
  };

  const clearAllData = () => {
    if (mode === 'live') {
      console.warn('[useRateCard] Live mode clear not yet implemented via API');
      return false;
    }
    try {
      RateCardStorageService.clearProjectRates(projectId);
      loadData();
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  };

  const loadRateChangeHistory = useCallback(async () => {
    if (mode !== 'live') {
      setRateChangeHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const response = await apiFetch<RateChangeHistoryEntry[]>(
        `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}/rate-change-history`
      );
      setRateChangeHistory(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading rate change history:', error);
      setRateChangeHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [mode, projectId]);

  return {
    summary,
    roleRates,
    rateChangeHistory,
    loading,
    historyLoading,
    updateRoleRate,
    updatePersonRate,
    clearAllData,
    refresh: loadData,
    loadRateChangeHistory,
  };
};
